import * as React from "react";
import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography
} from "@mui/material";
import { CheckCircle as CheckCircleIcon, Close as CloseIcon } from "@mui/icons-material";
import { formatRupees } from "./billzappPlans.jsx";
import AccountForms from "./accountForms.jsx";
import { useAccount } from "./accountContext.jsx";
import { loadRazorpayCheckout } from "./razorpayCheckout";
import { POS_PORTAL_URL } from "../data/billzappPlans";

const formatDate = (date) => new Date(date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

// Buys a BillZapp plan on the website, for someone who would rather pay straight away than use
// the free trial (or an owner renewing). The buyer logs in or creates their BillZapp account,
// then pays through Razorpay Checkout. The BillZapp server sets the price, confirms the payment,
// puts the plan on the account and emails the confirmation with the GST invoice.
export default function PlanPurchaseDialog({ plan, billingCycle, onClose }) {
  const { status, profile, request, refreshProfile, signOut } = useAccount();
  const [step, setStep] = useState("pay"); // "pay" | "paying" | "done"
  const [message, setMessage] = useState("");
  // Once paid: { planName, periodEnd }, or {} while the payment is still being confirmed.
  const [result, setResult] = useState(null);

  const isAnnual = billingCycle === "annual";
  const pricePaise = isAnnual ? plan.annual.amountPaise : plan.amountPaise;
  const period = isAnnual ? "year" : "month";
  const title = `BillZapp ${plan.name} (${isAnnual ? "Yearly" : "Monthly"})`;
  const needsAccount = status !== "signedIn" && step !== "done";

  const handlePay = async () => {
    setMessage("");
    setStep("paying");
    try {
      // Sent with the website login, so the server puts the plan on this account.
      const [order] = await Promise.all([
        request("/billing/website/createOrder", { method: "POST", body: { planId: plan.id, billingCycle } }),
        loadRazorpayCheckout(),
      ]);

      const checkout = new window.Razorpay({
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        order_id: order.orderId,
        name: "Sateri Electronic Services",
        description: `${title} - 1 ${period}`,
        prefill: { name: profile.businessName, email: profile.email, contact: profile.phone },
        theme: { color: "#1976d2" },
        handler: async (response) => {
          try {
            setResult(await request("/billing/website/verifyPayment", { method: "POST", body: response }));
            refreshProfile().catch(() => {}); // My Account shows the new plan
          } catch (err) {
            // The money has gone through; Razorpay's webhook confirms it and sends the email.
            setResult({});
          }
          setStep("done");
        },
        modal: {
          ondismiss: () => {
            setStep("pay");
            setMessage((current) => current || "Payment was cancelled. You can try again.");
          },
        },
      });
      checkout.on("payment.failed", (response) => {
        setMessage(response.error?.description || "The payment failed. Please try again.");
      });
      checkout.open();
    } catch (err) {
      setStep("pay");
      setMessage(err.message);
    }
  };

  return (
    <Dialog
      open
      onClose={step === "paying" ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      // Razorpay's window opens on top of this dialog and needs the keyboard.
      disableEnforceFocus={step === "paying"}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
        {step === "done" ? "Payment successful" : `Buy ${title}`}
        <IconButton onClick={onClose} size="small" aria-label="close" disabled={step === "paying"}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {step === "done" ? (
        <DialogContent>
          <Stack alignItems="center" textAlign="center" spacing={1.5} sx={{ py: 1 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 56 }} />
            {result.periodEnd ? (
              <>
                <Typography variant="h6" fontWeight="bold">
                  BillZapp {result.planName} is active
                </Typography>
                <Typography color="text.secondary">
                  It is on <b>{profile?.businessName}</b> until <b>{formatDate(result.periodEnd)}</b>. We have
                  emailed the confirmation and your GST invoice to <b>{profile?.email}</b>.
                </Typography>
              </>
            ) : (
              <Typography color="text.secondary">
                Thank you! We are still confirming your payment. Once it is confirmed your plan is
                active and the confirmation is emailed to you; you can check it under{" "}
                <Link href="/account?tab=purchases">My Account</Link>.
              </Typography>
            )}
          </Stack>
          <Divider sx={{ my: 3 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Log in to BillZapp with the same username and password, on the web or in the Android app.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button href={POS_PORTAL_URL} variant="contained" size="large" fullWidth>
              Open BillZapp
            </Button>
            <Button href="/account?tab=purchases" variant="outlined" size="large" fullWidth>
              View my purchases
            </Button>
          </Stack>
        </DialogContent>
      ) : (
        <DialogContent>
          <Stepper activeStep={needsAccount ? 0 : 1} alternativeLabel sx={{ mb: 3 }}>
            <Step><StepLabel>Your BillZapp account</StepLabel></Step>
            <Step><StepLabel>Payment</StepLabel></Step>
          </Stepper>

          {status === "checking" ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : needsAccount ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Your plan goes onto your BillZapp account. Create one in a minute, or log in if you
                already have one.
              </Typography>
              {message && <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>}
              <AccountForms planId={plan.id} initialTab="signup" onDone={() => setMessage("")} />
            </>
          ) : (
            <>
              <Alert severity="info" icon={false} sx={{ mb: 2 }}>
                Buying for <b>{profile.businessName}</b> (username {profile.username}).{" "}
                <Link component="button" type="button" onClick={signOut} disabled={step === "paying"} sx={{ verticalAlign: "baseline" }}>
                  Not you? Log out
                </Link>
              </Alert>
              {profile.planId !== plan.id && profile.planName && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Your account moves from {profile.planName} to {plan.name} once you pay.
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Any time left on your account, including free trial days, is kept, and the paid {period} is
                added after it.
              </Typography>
              {message && <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>}

              <Stack spacing={0.75} sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography color="text.secondary">BillZapp {plan.name} (1 {period})</Typography>
                  <Typography>{formatRupees(pricePaise)}</Typography>
                </Box>
                <Divider />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography fontWeight="bold">Total (incl. GST)</Typography>
                  <Typography fontWeight="bold">{formatRupees(pricePaise)}</Typography>
                </Box>
              </Stack>

              <Button onClick={handlePay} variant="contained" size="large" fullWidth disabled={step === "paying"}>
                {step === "paying" ? <CircularProgress size={26} color="inherit" /> : `Pay ${formatRupees(pricePaise)}`}
              </Button>
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1.5 }}>
                Payments are processed securely by Razorpay. The confirmation and GST invoice are emailed
                to {profile.email || "your account's email"}.
              </Typography>
            </>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
}
